import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LoginIcon from '@mui/icons-material/Login';
import CancelIcon from '@mui/icons-material/Cancel';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import axiosInstance from '../../../main/initializers/axios';
import IArea from '../../../main/interfaces/IArea';
import IPagination from '../../../main/interfaces/IPagination';
import {
  IUMBReservation,
  IUMBReservationListResponse,
  UMBReservationDayStatus,
  UMBReservationStatus,
} from '../../../main/interfaces/IUMBReservation';
import CreateUmbReservationDialog from './CreateUmbReservationDialog';
import UmbReservationDetailsDialog from './UmbReservationDetailsDialog';
import '../../../main/components/grid/GeneralTable.css';
import './UmbReservationsPage.css';

const defaultPagination: IPagination = {
  count: 0,
  page: 1,
  itemPerPage: 20,
  itemsPerPage: [10, 20, 50, 100],
};

const UmbReservationsPage = () => {
  const { t, i18n } = useTranslation();
  const [reservations, setReservations] = useState<IUMBReservation[]>([]);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [pagination, setPagination] = useState<IPagination>(defaultPagination);
  const [filters, setFilters] = useState({ search: '', fromDate: '', toDate: '', status: 0 });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<IUMBReservation | null>(null);
  const [details, setDetails] = useState<IUMBReservation | null>(null);

  const loadReservations = useCallback(
    async (page = pagination.page, itemPerPage = pagination.itemPerPage) => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await axiosInstance.post<IUMBReservationListResponse>(ApiConstants.umbReservation.listCt, {
          fromDate: appliedFilters.fromDate || null,
          toDate: appliedFilters.toDate || null,
          status: appliedFilters.status || null,
          search: appliedFilters.search.trim() || null,
          pagination: { page, itemPerPage, count: pagination.count },
        });
        setReservations(response.data.items ?? []);
        setPagination((current) => ({
          ...current,
          page: response.data.pagination?.page ?? page,
          itemPerPage: response.data.pagination?.itemPerPage ?? itemPerPage,
          count: response.data.pagination?.count ?? 0,
        }));
      } catch {
        setLoadError(t('umbReservations.errors.list'));
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, pagination.count, pagination.itemPerPage, pagination.page, t],
  );

  useEffect(() => {
    loadReservations(1, defaultPagination.itemPerPage);
    axiosInstance.get(ApiConstants.area.list).then((response) => setAreas(response.data ?? [])).catch(() => setAreas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const openDetails = async (id: number) => {
    const response = await axiosInstance.get(ApiConstants.umbReservation.byId(id));
    setDetails(response.data);
  };

  const refreshAfterChange = async (id?: number) => {
    await loadReservations();
    if (id) {
      const response = await axiosInstance.get(ApiConstants.umbReservation.byId(id));
      setDetails(response.data);
    } else {
      setDetails(null);
    }
  };

  const cancelFromList = async (reservation: IUMBReservation) => {
    if (!window.confirm(t('umbReservations.cancelConfirmation'))) return;
    await axiosInstance.post(ApiConstants.umbReservation.cancel, {
      reservationId: reservation.id,
      reason: null,
    });
    await loadReservations();
  };

  const canEditReservation = (reservation: IUMBReservation) =>
    reservation.status === UMBReservationStatus.Confirmed
    && !reservation.items.some((item) =>
      item.days.some((day) => day.status === UMBReservationDayStatus.CheckedIn || day.umbInventoryId),
    );

  const canDeleteReservation = (reservation: IUMBReservation) =>
    !reservation.items.some((item) =>
      item.days.some((day) => day.status === UMBReservationDayStatus.CheckedIn || day.umbInventoryId),
    );

  const deleteReservation = async (reservation: IUMBReservation) => {
    if (!window.confirm(t('umbReservations.deleteConfirmation'))) return;
    try {
      await axiosInstance.delete(ApiConstants.umbReservation.delete, {
        data: { reservationId: reservation.id },
      });
      toast.success(t('umbReservations.messages.deleted'));
      setDetails(null);
      setEditing(null);
      await loadReservations();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message
          ?? error?.response?.data?.Message
          ?? t('umbReservations.errors.delete'),
      );
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.count / pagination.itemPerPage));

  return (
    <BasicCli>
      <div className="umbr-page bcPageContent">
        <header className="bcPageHeader umbr-page-header">
          <div>
            <h1 className="bcPageTitle">{t('umbReservations.title')}</h1>
            {/* <p>{t('umbReservations.subtitle')}</p> */}
          </div>
          <button type="button" className="ui-control ui-btn ui-btn--primary umbr-new-reservation--desktop" onClick={() => setCreateOpen(true)}>
            <AddIcon />
            {t('umbReservations.newReservation')}
          </button>
        </header>

        <section className="umbr-filters">
          <label className="umbr-filter-search">
            <span>{t('generic.search')}</span>
            <input
              value={filters.search}
              placeholder={t('umbReservations.searchPlaceholder')}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              onKeyDown={(event) => event.key === 'Enter' && setAppliedFilters(filters)}
            />
          </label>
          <label className="umbr-filter-from-date">
            <span>{t('umbReservations.fromDate')}</span>
            <input type="date" value={filters.fromDate} onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))} />
          </label>
          <label className="umbr-filter-to-date">
            <span>{t('umbReservations.toDate')}</span>
            <input type="date" value={filters.toDate} onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))} />
          </label>
          <label className="umbr-filter-status">
            <span>{t('umbReservations.status')}</span>
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: Number(event.target.value) }))}>
              <option value={0}>{t('umbReservations.allStatuses')}</option>
              <option value={UMBReservationStatus.Confirmed}>{t('umbReservations.statuses.1')}</option>
              <option value={UMBReservationStatus.Completed}>{t('umbReservations.statuses.2')}</option>
              <option value={UMBReservationStatus.Cancelled}>{t('umbReservations.statuses.3')}</option>
            </select>
          </label>
          <button type="button" className="ui-control ui-btn ui-btn--primary umbr-filter-submit" onClick={() => setAppliedFilters(filters)}>
            {t('generic.filter')}
          </button>
          <button
            type="button"
            className="ui-control ui-btn ui-btn--primary umbr-new-reservation--mobile"
            onClick={() => setCreateOpen(true)}
          >
            <AddIcon />
            {t('umbReservations.newReservation')}
          </button>
        </section>

        <section className="umbr-list-panel">
          {loadError && <div className="umbr-alert is-error">{loadError}</div>}
          {loading ? (
            <div className="umbr-empty">{t('umbReservations.loading')}</div>
          ) : reservations.length === 0 ? (
            <div className="umbr-empty">
              <BeachAccessIcon />
              <strong>{t('umbReservations.emptyTitle')}</strong>
              <span>{t('umbReservations.emptySubtitle')}</span>
            </div>
          ) : (
            <div className="umbr-table-wrap">
              <table className="umbr-table">
                <thead>
                  <tr>
                    <th>{t('umbReservations.customer')}</th>
                    <th>{t('umbReservations.dateRange')}</th>
                    <th>{t('umbReservations.umbrellas')}</th>
                    <th>{t('umbReservations.deposit')}</th>
                    <th>{t('umbReservations.amountDue')}</th>
                    <th>{t('umbReservations.status')}</th>
                    <th>{t('umbReservations.todayState')}</th>
                    <th>{t('umbReservations.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((reservation) => (
                    <tr key={reservation.id}>
                      <td className="umbr-mobile-customer" data-label={t('umbReservations.customer')}>
                        <strong>{reservation.customerName}</strong>
                        <span>{reservation.customerPhone || '-'}</span>
                      </td>
                      <td className="umbr-mobile-dates" data-label={t('umbReservations.dateRange')}>
                        <strong>{formatDate(reservation.startDate, i18n.language)}</strong>
                        <span>{formatDate(reservation.endDate, i18n.language)}</span>
                      </td>
                      <td className="umbr-mobile-umbrellas" data-label={t('umbReservations.umbrellas')}>{reservation.umbrellaCount}</td>
                      <td data-label={t('umbReservations.deposit')} className="umbr-money umbr-mobile-deposit">
                        {formatMoney(reservation.depositAmount ?? 0, i18n.language)}
                      </td>
                      <td data-label={t('umbReservations.amountDue')} className="umbr-money is-due umbr-mobile-due">
                        {formatMoney(getAmountDue(reservation), i18n.language)}
                      </td>
                      <td className="umbr-mobile-status" data-label={t('umbReservations.status')}>
                        <span className={`umbr-status is-${reservation.status}`}>{t(`umbReservations.statuses.${reservation.status}`)}</span>
                      </td>
                      <td className="umbr-mobile-today" data-label={t('umbReservations.todayState')}>
                        {reservation.todayReservedCount > 0
                          ? t('umbReservations.todayReserved', { count: reservation.todayReservedCount })
                          : reservation.todayCheckedInCount > 0
                            ? t('umbReservations.todayCheckedIn', { count: reservation.todayCheckedInCount })
                            : t('umbReservations.noTodayActivity')}
                      </td>
                      <td className="umbr-mobile-actions" data-label={t('umbReservations.actions')}>
                        <div className="umbr-row-actions">
                          <button type="button" onClick={() => openDetails(reservation.id)} title={t('generic.View')}>
                            <VisibilityIcon />
                          </button>
                          {reservation.todayReservedCount > 0 && reservation.status === UMBReservationStatus.Confirmed && (
                            <button type="button" onClick={() => openDetails(reservation.id)} title={t('umbReservations.checkIn')}>
                              <LoginIcon />
                            </button>
                          )}
                          {canEditReservation(reservation) && (
                            <button type="button" onClick={() => setEditing(reservation)} title={t('umbReservations.editReservation')}>
                              <EditIcon />
                            </button>
                          )}
                          {reservation.status !== UMBReservationStatus.Cancelled && (
                            <button type="button" className="is-danger" onClick={() => cancelFromList(reservation)} title={t('generic.cancel')}>
                              <CancelIcon />
                            </button>
                          )}
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => deleteReservation(reservation)}
                            disabled={!canDeleteReservation(reservation)}
                            title={t('umbReservations.deleteReservation')}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="gtBottomBar umbr-pagination-bar">
            <div className="gtBottomControls">
              <div className="gtPageStepper">
                <Pagination
                  count={totalPages}
                  page={pagination.page}
                  onChange={(_, page) => loadReservations(page)}
                  className="gtPagination"
                />
              </div>
              <FormControl className="gtPerPageWrap">
                <InputLabel id="umbr-per-page-label" className="gtLabel">
                  {t('generic.rows')}
                </InputLabel>
                <Select
                  labelId="umbr-per-page-label"
                  value={pagination.itemPerPage}
                  label={t('generic.rows')}
                  onChange={(event) => loadReservations(1, Number(event.target.value))}
                  className="gtPerPage gtSelect"
                  input={<OutlinedInput className="gtOutlinedInput" />}
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: { className: 'gtMenuPaper' },
                    MenuListProps: { className: 'gtMenuList' },
                  }}
                >
                  {pagination.itemsPerPage.map((value) => (
                    <MenuItem key={value} value={value} className="gtMenuItem">
                      {value} / {t('generic.page')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </section>

        {createOpen && (
          <CreateUmbReservationDialog
            areas={areas}
            onClose={() => setCreateOpen(false)}
            onSaved={() => {
              setCreateOpen(false);
              loadReservations(1);
            }}
          />
        )}
        {editing && (
          <CreateUmbReservationDialog
            areas={areas}
            reservation={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setDetails(null);
              loadReservations();
            }}
          />
        )}
        {details && (
          <UmbReservationDetailsDialog
            reservation={details}
            onClose={() => setDetails(null)}
            onChanged={() => refreshAfterChange(details.id)}
            onEdit={() => {
              setEditing(details);
              setDetails(null);
            }}
            onDelete={() => deleteReservation(details)}
          />
        )}
      </div>
    </BasicCli>
  );
};

const formatDate = (value: string, locale: string) => {
  const date = value?.slice(0, 10);
  return date ? new Date(`${date}T00:00:00`).toLocaleDateString(locale) : '-';
};

const getAmountDue = (reservation: IUMBReservation) => {
  const total = reservation.items.reduce(
    (reservationTotal, item) => reservationTotal + Number(item.lockedDailyPrice || 0) * item.days.length,
    0,
  );
  return Math.max(0, total - Number(reservation.depositAmount || 0));
};

const formatMoney = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export default UmbReservationsPage;
